/**
 * Nav-item visibility — the single predicate every navigation surface uses.
 *
 * A `NavItem` can carry its own gates (`requiresAdmin`, `requiresModuleAdmin`,
 * `requiresSysadmin`, `requiresGoogleConnection`, `requiresMicrosoftConnection`,
 * `requiresForwarderMode`, `requiresMultiCompany`) that are INDEPENDENT of its
 * module's gate: an
 * admin-only page can live inside a module every employee may open. The sidebar,
 * the module picker and global search must all apply that gate identically —
 * they previously did not, so search offered pages the sidebar hid — hence one
 * exported predicate rather than a filter expression per surface.
 *
 * Note the deliberate difference from the MODULE-level gate in
 * `getVisibleModules` (modules.config.tsx): there a sysadmin satisfies
 * `requiresAdmin`, because a sysadmin must be able to reach admin modules. At
 * ITEM level there is no such fallback — `is_admin` and `is_sysadmin` are
 * independent flags (`is_sysadmin` derives from the tenant type), and the
 * sidebar has always gated item-level `requiresAdmin` on `isAdmin` alone. The
 * two levels are not to be unified.
 *
 * This is result LISTING, not authorization: route guards and API authz remain
 * the enforcement point.
 */

import type { NavItem } from '../config/modules.config';

/** Everything the nav-item gates are evaluated against, resolved per viewer. */
export interface NavItemAccess {
  /** `user.is_admin` — tenant admin. NOT implied by sysadmin at item level. */
  isAdmin: boolean;
  /** `user.is_sysadmin` — derived from the tenant type, independent of admin. */
  isSysadmin: boolean;
  /**
   * Module-admin test for a PERMISSION module name — the backend
   * `require_module_admin` mirror from `useIsModuleAdmin` (tenant admin OR the
   * `{module}.settings.manage` role OR a T11 `admin` access level). A predicate
   * rather than a flag because a nav item is gated on ITS OWN module, and one
   * access object is shared across every module's items.
   */
  isModuleAdmin: (permissionModule: string) => boolean;
  /** Google account connected (Gmail / Drive nav items). */
  isGoogleConnected: boolean;
  /** Microsoft account connected (Outlook / OneDrive nav items). */
  isMicrosoftConnected: boolean;
  /** Tenant persona flag `sells_freight_services` (Forwarder TMS nav items). */
  sellsFreightServices: boolean;
  /**
   * Whether the tenant has more than one company (legal entity) — the
   * multi-company "hide-when-single" gate. Defaults to hidden while the
   * company list is still loading, so a gated item never flashes in and out
   * of the sidebar on every page load.
   */
  hasMultipleCompanies: boolean;
}

/**
 * Whether this nav item may be shown UNPROMPTED — in the sidebar, or in a
 * resting-state suggestion list the user did not type a query for.
 *
 * Orthogonal to {@link isNavItemVisible}, which asks whether the VIEWER may
 * have it at all. This asks whether the ITEM is a listed destination or a
 * search-index entry: a `searchOnly` item exists to ANSWER a query
 * (`NavItem.searchOnly`), so a surface that offers pages without being asked —
 * the sidebar's nav list, `getSuggestedPages`' empty-query Cmd+K state,
 * `MainLayout`'s "does this module have a sidebar at all" count — must apply
 * this predicate as well.
 *
 * The two are applied together, never one instead of the other: a listed item
 * the viewer may not have is still hidden, and a searchOnly item the viewer MAY
 * have is still not listed.
 */
export function isListedNavItem(navItem: NavItem): boolean {
  return !navItem.searchOnly;
}

/** Whether this viewer may see (and navigate to) the given nav item. */
export function isNavItemVisible(navItem: NavItem, access: NavItemAccess): boolean {
  if (navItem.requiresGoogleConnection && !access.isGoogleConnected) return false;
  if (navItem.requiresMicrosoftConnection && !access.isMicrosoftConnected) return false;
  if (navItem.requiresAdmin && !access.isAdmin) return false;
  if (navItem.requiresModuleAdmin && !navItem.permissionModules.some(access.isModuleAdmin))
    return false;
  if (navItem.requiresSysadmin && !access.isSysadmin) return false;
  if (navItem.requiresForwarderMode && !access.sellsFreightServices) return false;
  if (navItem.requiresMultiCompany && !access.hasMultipleCompanies) return false;
  return true;
}
